// NOTE: Validation for the map contract.
//
// The schema is where the budget is enforced and where a coordinate is checked
// for being a coordinate at all. Both messages are written to be acted on by a
// model rather than only read by a developer: they name the field, the limit
// and the way out, because the model is the one that has to fix the spec.

import { z } from "zod";

import {
  MAP_INVALID_JSON_ERROR,
  MAP_LATITUDE_MAX,
  MAP_LATITUDE_MIN,
  MAP_LONGITUDE_MAX,
  MAP_LONGITUDE_MIN,
  MAX_MAP_AREA_POINTS,
  MAX_MAP_FOCAL_MARKERS,
  MAX_MAP_LABEL_LENGTH,
  MAX_MAP_MARKERS,
  MAX_MAP_ROUTE_POINTS,
  MAX_MAP_SUBLABEL_LENGTH,
  MAX_MAP_SUBTITLE_LENGTH,
  MAX_MAP_TITLE_LENGTH,
  MIN_MAP_AREA_POINTS,
  MIN_MAP_ROUTE_POINTS,
} from "../constants/map";
import { EMapMarkerKind, type TParseMapResult } from "../types/map";

const latitudeSchema = z
  .number()
  .finite()
  .min(MAP_LATITUDE_MIN, {
    message: `Latitude must be between ${MAP_LATITUDE_MIN} and ${MAP_LATITUDE_MAX} degrees — check that latitude and longitude have not been swapped`,
  })
  .max(MAP_LATITUDE_MAX, {
    message: `Latitude must be between ${MAP_LATITUDE_MIN} and ${MAP_LATITUDE_MAX} degrees — check that latitude and longitude have not been swapped`,
  });

const longitudeSchema = z
  .number()
  .finite()
  .min(MAP_LONGITUDE_MIN, {
    message: `Longitude must be between ${MAP_LONGITUDE_MIN} and ${MAP_LONGITUDE_MAX} degrees`,
  })
  .max(MAP_LONGITUDE_MAX, {
    message: `Longitude must be between ${MAP_LONGITUDE_MIN} and ${MAP_LONGITUDE_MAX} degrees`,
  });

const coordinateSchema = z
  .object({ lat: latitudeSchema, lon: longitudeSchema })
  .strict();

const labelSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_MAP_LABEL_LENGTH, {
    message: `A label is at most ${MAX_MAP_LABEL_LENGTH} characters — shorten the wording rather than running over, or the whole map is rejected`,
  });

const markerSchema = z
  .object({
    id: z.string().trim().min(1).max(64),
    label: labelSchema,
    sublabel: z.string().trim().min(1).max(MAX_MAP_SUBLABEL_LENGTH).optional(),
    lat: latitudeSchema,
    lon: longitudeSchema,
    kind: z.nativeEnum(EMapMarkerKind).optional(),
    focal: z.boolean().optional(),
  })
  .strict();

const routeSchema = z
  .object({
    label: z.string().trim().min(1).max(MAX_MAP_LABEL_LENGTH).optional(),
    points: z
      .array(coordinateSchema)
      .min(MIN_MAP_ROUTE_POINTS, {
        message: `A route needs at least ${MIN_MAP_ROUTE_POINTS} points — a single point is a marker, not a route`,
      })
      .max(MAX_MAP_ROUTE_POINTS, {
        message: `A route carries at most ${MAX_MAP_ROUTE_POINTS} points — simplify the line rather than listing every turn`,
      }),
  })
  .strict();

const areaSchema = z
  .object({
    label: z.string().trim().min(1).max(MAX_MAP_LABEL_LENGTH).optional(),
    points: z
      .array(coordinateSchema)
      .min(MIN_MAP_AREA_POINTS, {
        message: `An area needs at least ${MIN_MAP_AREA_POINTS} points to enclose anything`,
      })
      .max(MAX_MAP_AREA_POINTS, {
        message: `An area carries at most ${MAX_MAP_AREA_POINTS} points — simplify the outline`,
      }),
  })
  .strict();

export const mapSpecSchema = z
  .object({
    title: z.string().trim().min(1).max(MAX_MAP_TITLE_LENGTH).optional(),
    subtitle: z.string().trim().min(1).max(MAX_MAP_SUBTITLE_LENGTH).optional(),
    markers: z
      .array(markerSchema)
      .min(1, {
        message:
          "A map needs at least one marker — a basemap with nothing on it answers no question",
      })
      .max(MAX_MAP_MARKERS, {
        message: `A map carries at most ${MAX_MAP_MARKERS} markers — group them, or draw one map per region`,
      }),
    route: routeSchema.optional(),
    area: areaSchema.optional(),
  })
  .strict()
  .superRefine((spec, context) => {
    const ids = spec.markers.map((marker) => marker.id);

    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Every marker id must be unique — two markers share the same id",
        path: ["markers"],
      });
    }

    const focalCount = spec.markers.filter(
      (marker) => marker.focal === true,
    ).length;

    if (focalCount > MAX_MAP_FOCAL_MARKERS) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `At most ${MAX_MAP_FOCAL_MARKERS} markers may set focal — marking everything focal is the same as marking nothing`,
        path: ["markers"],
      });
    }
  });

// NOTE: Issues are flattened into one readable sentence per problem, matching
// the diagram contract: the model sees this text and has to be able to act on
// it without guessing which field it is about.
const formatIssues = (error: z.ZodError): string =>
  error.issues
    .map((issue) => {
      const path = issue.path.join(".");
      return path.length > 0 ? `${path}: ${issue.message}` : issue.message;
    })
    .join("; ");

export const parseMapSpec = (input: unknown): TParseMapResult => {
  const result = mapSpecSchema.safeParse(input);

  if (result.success) {
    return { success: true, data: result.data };
  }

  return { success: false, error: formatIssues(result.error) };
};

// NOTE: One fenced ```map block's source, turned into a spec or into the reason
// it cannot be drawn.
//
// Both clients hold the same three-way answer — draw it, hold the skeleton, or
// fall back to the source — and the half that decides which is this parse.
// Streaming makes it unavoidable: for most of a block's life the JSON is
// truncated, so the parse THROWS rather than returning an error, and a client
// that forgot the try would take the transcript down with a half-written
// marker. Written once here, that is one place to get right.
//
// NSAI-5931 — the reason travels with the failure rather than being dropped,
// so the fallback can say which field broke the contract and how to fix it.
export const parseMapBlockSource = (rawJson: string): TParseMapResult => {
  try {
    const parsed: unknown = JSON.parse(rawJson);

    return parseMapSpec(parsed);
  } catch {
    return { success: false, error: MAP_INVALID_JSON_ERROR };
  }
};
